import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, delay, map, Observable, of, Subject, tap } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { SessionService } from "src/app/services/session.service";

export interface SubtaskComment {
  text: string;
  author: string;
  date: string;
}

export interface SubtaskModel {
  subtask_id: string;
  name: string;
  taskType: string;
  startDate: string | null;
  endDate: string | null;
  remindMe: boolean;
  remindDate: string | null;
  remindTime: string | null;
  priorityLevel: string;
  status: string;
  assignTo: string;
  tagType: string;
  tags: number[];
  description: string;
  attachments: File[];
  comments: SubtaskComment[];
  /** Comment count from the API bulk response. Mirrors TaskModel._commentCount. */
  _commentCount?: number;
  /** Attachment count from the API bulk response. Mirrors TaskModel._attachmentCount. */
  _attachmentCount?: number;
}

export interface TaskModel {
  task_id: string;
  taskName: string;
  taskType: string;
  assignTo: string;
  /** Full assignee list preserved from the API response so kanban cards can
   *  render stacked avatar circles instead of a single name. Optional for back-
   *  compat with the mock data branch. */
  assignees?: { assignee_email: string }[];
  startDate: string | null;
  endDate: string | null;
  remindMe: boolean;
  remindDate: string | null;
  remindTime: string | null;
  status: string;
  priorityLevel: string;
  tagType: string;
  tags: number[];
  description: string;
  taskStatus: boolean;
  isCancelled: boolean;
  subtasks: SubtaskModel[];
  /** Total subtask count, populated from the in-memory all-subtasks store. */
  _subCount?: number;
  /** Done subtask count, populated from the in-memory all-subtasks store. */
  _subDone?: number;
  /** Comment count — populated from get-all, updated in real time via
   *  emitAndApplyCountChange when comments are posted or deleted. */
  _commentCount?: number;
  /** Attachment count — populated from get-all, updated in real time via
   *  emitAndApplyCountChange when attachments are uploaded or deleted. */
  _attachmentCount?: number;
  /** Original API payload preserved for templates that need direct access to
   *  task_status_name, task_priority_name, task_type_name, etc. */
  _raw?: any;
}

@Injectable({
  providedIn: "root",
})
export class TaskMgmtService {
  private tasks$ = new BehaviorSubject<TaskModel[]>([]);
  private loaded = false;
  private useMockData = false;

  /** In-memory store of every subtask in the current brand. Populated once at startup
   *  via `refreshAllSubtasks()` and mutated locally on create/update/drag-drop so the
   *  UI never has to re-fetch. */
  private allSubtasks$ = new BehaviorSubject<any[]>([]);

  /** Lookup lists loaded from the workflow service and pushed in via the public
   *  setters below. All ID-to-name resolution is driven from these lists — no
   *  hardcoded numeric IDs anywhere. */
  private statusList: { task_status_id: any; status_name: string }[] = [];
  private priorityList: { task_priority_id: any; priority_name: string }[] = [];
  private typeList: { task_type_id: any; type_name: string }[] = [];

  /** Emits when comment or attachment count changes for a task. Subscribers
   *  (e.g. the kanban board) update badges in real time without re-fetching. */
  taskCountChanged$ = new Subject<{ taskId: number; commentCount?: number; attachmentCount?: number }>();

  /** Emits when comment or attachment count changes for a SUBTASK. Subscribers
   *  (e.g. the subtask sidebar) re-derive their local subtask list so badges
   *  on compact cards and table detail rows update in real time.
   *  `parentTaskId` is resolved from the in-memory store so the subscriber can
   *  refresh the correct master-detail grid row without a separate lookup. */
  subtaskCountChanged$ = new Subject<{ subtaskId: number; parentTaskId: number | null; commentCount?: number; attachmentCount?: number }>();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private sessionService: SessionService
  ) { }

  /** Fetch all tasks from real API (auto-injects client_key/employee_key/module_key via ApiService.post). */
  loadTasksFromApi(): Observable<any> {
    // TODO: Replace hardcoded brand_key with dynamic selection from the toolbar brand dropdown
    return this.apiService.post('api/tasks/get-all', { brand_key: 1 });
  }

  /** Fetch tasks filtered by a tag (e.g. the patient / HCP the board is opened
   *  for when embedded in the global sidebar). Hits the SAME get-all endpoint
   *  with the extra `tag_id` / `tag_category_id` filter and maps the response to
   *  TaskModel[] WITHOUT mutating the shared `tasks$` store / `loaded` flag — so
   *  the main board's cached, unfiltered list is never disturbed. */
  loadTasksByTagFromApi(tagId: any, tagCategoryId: any): Observable<TaskModel[]> {
    return this.apiService
      .post('api/tasks/get-all', {
        brand_key: 1,
        tag_id: tagId,
        tag_category_id: tagCategoryId,
      })
      .pipe(map((res: any) => this.mapApiResponseToTasks(res)));
  }

  /** Create a task via real API (payload built by mapToCreatePayload). */
  createTaskApi(payload: any): Observable<any> {
    return this.apiService.post('api/tasks/create', payload);
  }

  /** Update an existing task via real API. */
  updateTaskApi(payload: any): Observable<any> {
    return this.apiService.post('api/tasks/update', payload);
  }

  /** Update only the status of a task via real API. */
  updateTaskStatusApi(payload: { task_id: number; task_status_id: number; updated_by: string | null }): Observable<any> {
    return this.apiService.post('api/tasks/update-status', payload);
  }

  /** Fetch the freshest version of a single task (or subtask — same endpoint
   *  per the API spec) by id. Used by the kanban-card edit / comment shortcuts
   *  and the subtask sidebar to refresh stale in-memory data immediately
   *  before opening the edit dialog. */
  getTaskByIdFromApi(taskId: number): Observable<any> {
    return this.apiService.post('api/tasks/get-by-id', { task_id: taskId });
  }

  // ==========================================================
  //  Task attachments API
  //  Same endpoints work for tasks and subtasks — subtasks are
  //  tasks with a parent_task_id, the API doesn't distinguish.
  // ==========================================================

  /** List every attachment on a task (or subtask). */
  getTaskAttachments(taskId: number): Observable<any> {
    return this.apiService.post('api/tasks/attachments/get', { task_id: taskId });
  }

  /** Upload a single attachment. This is the ONLY task-mgmt endpoint that uses
   *  multipart/form-data, so it bypasses ApiService.post (which hardcodes JSON)
   *  and goes directly through HttpClient. The `MyInterceptor` already detects
   *  FormData bodies and skips its `Content-Type: application/json` header, so
   *  the browser sets the correct `multipart/form-data; boundary=...` header
   *  automatically. Auth token + user_id header are still injected by the
   *  interceptor. `uploadedBy` is the logged-in user's email. */
  uploadTaskAttachment(taskId: number, file: File, uploadedBy: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('task_id', String(taskId));
    formData.append('uploaded_by', uploadedBy);
    return this.http.post(`${this.apiService.apiBaseUrl}/api/tasks/attachments/upload`, formData);
  }

  /** Resolve a short-lived signed URL for an attachment so the browser can
   *  download it directly from S3 without routing the binary through the API. */
  getAttachmentPresignedUrl(taskId: number, attachmentId: number): Observable<any> {
    return this.apiService.post('api/tasks/attachments/presigned-url', {
      task_id: taskId,
      attachment_id: attachmentId,
    });
  }

  /** Delete an attachment. */
  deleteTaskAttachment(taskId: number, attachmentId: number): Observable<any> {
    return this.apiService.post('api/tasks/attachments/delete', {
      task_id: taskId,
      attachment_id: attachmentId,
    });
  }

  /** Fetch every subtask in the current brand in a single call. */
  loadAllSubtasksFromApi(): Observable<any> {
    // TODO: Replace hardcoded brand_key with dynamic selection from the toolbar brand dropdown
    return this.apiService.post('api/tasks/get-all-sub-tasks', { brand_key: 1 });
  }

  /** Fetch the brand list from the dashboard filter API. Used by the task
   *  management toolbar brand selector. ApiService.post auto-injects auth
   *  keys (client_key, employee_key, module_key) so an empty body is
   *  sufficient — same pattern used by email-template-designer.component.ts. */
  loadBrands(): Observable<any> {
    return this.apiService.post('PSS/get-brand-filter', {});
  }

  /** Fetch the user list for the assignee search dropdown. Uses the same
   *  endpoint as the impersonation component. Returns array of objects with
   *  { user_id, usr_display_nm, ... }. `keyword` filters server-side. */
  getAllUserForImpersonate(keyword: string = ''): Observable<any> {
    return this.apiService.get('api/get-all-users');
  }

  // ==========================================================
  //  Task comments / activity API
  //  Same endpoints work for both tasks and subtasks — subtasks
  //  are tasks with a parent_task_id, the API doesn't distinguish.
  // ==========================================================

  /** Fetch all comments for a task (or subtask). */
  getTaskComments(taskId: number): Observable<any> {
    return this.apiService.post('api/tasks/comments/get', { task_id: taskId });
  }

  /** Post a new comment on a task. `createdBy` is the logged-in user's EMAIL. */
  addTaskComment(taskId: number, commentText: string, createdBy: string): Observable<any> {
    return this.apiService.post('api/tasks/comments/add', {
      task_id: taskId,
      comment_text: commentText,
      created_by: createdBy,
    });
  }

  /** Edit an existing comment (any user can edit any comment per spec). */
  editTaskComment(taskId: number, taskUpdateId: number, commentText: string): Observable<any> {
    return this.apiService.post('api/tasks/comments/edit', {
      task_id: taskId,
      task_update_id: taskUpdateId,
      comment_text: commentText,
    });
  }

  /** Delete a comment (any user can delete any comment per spec). */
  deleteTaskComment(taskId: number, taskUpdateId: number): Observable<any> {
    return this.apiService.post('api/tasks/comments/delete', {
      task_id: taskId,
      task_update_id: taskUpdateId,
    });
  }

  // ==========================================================
  //  All-subtasks store + derived selectors
  // ==========================================================

  /** Stream of every subtask in the in-memory store. */
  getAllSubtasks(): Observable<any[]> {
    return this.allSubtasks$.asObservable();
  }

  /** Synchronous snapshot of every subtask in the in-memory store. */
  getAllSubtasksSnapshot(): any[] {
    return this.allSubtasks$.getValue();
  }

  /** Hit the all-subtasks endpoint and replace the in-memory store with the result. */
  refreshAllSubtasks(): Observable<any[]> {
    return this.loadAllSubtasksFromApi().pipe(
      map((response: any) => {
        let subtasks: any[] = [];
        if (Array.isArray(response)) {
          subtasks = response;
        } else if (Array.isArray(response?.items)) {
          subtasks = response.items;
        } else if (Array.isArray(response?.data)) {
          subtasks = response.data;
        } else if (Array.isArray(response?.result)) {
          subtasks = response.result;
        } else if (Array.isArray(response?.subtasks)) {
          subtasks = response.subtasks;
        }
        this.allSubtasks$.next(subtasks);
        return subtasks;
      })
    );
  }

  /** Filter the in-memory store by parent_task_id. */
  getSubtasksForParent(parentTaskId: number): any[] {
    if (parentTaskId == null || isNaN(parentTaskId)) return [];
    return this.allSubtasks$.getValue().filter((s) => s?.parent_task_id === parentTaskId);
  }

  /** Total / done counts for a parent task, derived from the in-memory store. */
  getSubtaskCountsForParent(parentTaskId: number): { total: number; done: number } {
    const subs = this.getSubtasksForParent(parentTaskId);
    const done = subs.filter((s) => this.isDoneStatus(s?.task_status_id)).length;
    return { total: subs.length, done };
  }

  // ==========================================================
  //  Dynamic lookup setters / id<->name resolvers
  //  All ID resolution flows through these — no hardcoded ids.
  // ==========================================================

  setStatusList(statuses: { task_status_id: any; status_name: string }[]): void {
    this.statusList = Array.isArray(statuses) ? statuses : [];
  }
  setPriorityList(priorities: { task_priority_id: any; priority_name: string }[]): void {
    this.priorityList = Array.isArray(priorities) ? priorities : [];
  }
  setTypeList(types: { task_type_id: any; type_name: string }[]): void {
    this.typeList = Array.isArray(types) ? types : [];
  }

  getStatusIdByName(name: string | null | undefined): number | null {
    if (!name) return null;
    const target = String(name).toLowerCase().trim();
    const match = this.statusList.find(
      (s) => String(s?.status_name || '').toLowerCase().trim() === target
    );
    return match ? Number(match.task_status_id) : null;
  }
  getStatusNameById(id: number | null | undefined): string {
    if (id == null) return '';
    const match = this.statusList.find((s) => Number(s?.task_status_id) === Number(id));
    return match ? String(match.status_name || '') : '';
  }
  getPriorityIdByName(name: string | null | undefined): number | null {
    if (!name) return null;
    const target = String(name).toLowerCase().trim();
    const match = this.priorityList.find(
      (p) => String(p?.priority_name || '').toLowerCase().trim() === target
    );
    return match ? Number(match.task_priority_id) : null;
  }
  getPriorityNameById(id: number | null | undefined): string {
    if (id == null) return '';
    const match = this.priorityList.find((p) => Number(p?.task_priority_id) === Number(id));
    return match ? String(match.priority_name || '') : '';
  }
  getTypeIdByName(name: string | null | undefined): number | null {
    if (!name) return null;
    const target = String(name).toLowerCase().trim();
    const match = this.typeList.find(
      (t) => String(t?.type_name || '').toLowerCase().trim() === target
    );
    return match ? Number(match.task_type_id) : null;
  }
  getTypeNameById(id: number | null | undefined): string {
    if (id == null) return '';
    const match = this.typeList.find((t) => Number(t?.task_type_id) === Number(id));
    return match ? String(match.type_name || '') : '';
  }

  isDoneStatus(statusId: number | null | undefined): boolean {
    const name = this.getStatusNameById(statusId).toLowerCase().trim();
    return name === 'done' || name === 'closed' || name === 'complete' || name === 'completed';
  }
  isCancelledStatus(statusId: number | null | undefined): boolean {
    const name = this.getStatusNameById(statusId).toLowerCase().trim();
    return name === 'cancelled' || name === 'canceled';
  }

  /** Alias for `isDoneStatus` — returns true when the supplied status id maps
   *  to a "completion" name (Done / Closed / Complete / Completed). Used by
   *  the parent-task-completion guard. */
  isCompletionStatus(statusId: number | null | undefined): boolean {
    return this.isDoneStatus(statusId);
  }

  /** Synchronous validation: a parent task may only transition to a completion
   *  status when ALL of its subtasks are already Done or Cancelled. Reads the
   *  in-memory all-subtasks store via `getSubtasksForParent` — no API call.
   *
   *  Returns:
   *    - allowed: true when there are no subtasks, or when every subtask is
   *      Done/Cancelled.
   *    - allowed: false when at least one subtask is still Open / In Progress.
   *    - incompleteCount: number of subtasks blocking the transition.
   *    - message: human-readable snackbar text (singular/plural-aware).
   *
   *  TODO: This is a client-side guard. Add a backend validation that prevents
   *  completing a task with incomplete subtasks as a safety net for race
   *  conditions or direct API calls. */
  canCompleteTask(taskId: number | null | undefined): { allowed: boolean; incompleteCount: number; message: string } {
    if (taskId == null || isNaN(Number(taskId))) {
      return { allowed: true, incompleteCount: 0, message: '' };
    }
    const subtasks = this.getSubtasksForParent(Number(taskId));
    if (subtasks.length === 0) {
      return { allowed: true, incompleteCount: 0, message: '' };
    }
    const incomplete = subtasks.filter(
      (s) => !this.isDoneStatus(s?.task_status_id) && !this.isCancelledStatus(s?.task_status_id)
    );
    if (incomplete.length === 0) {
      return { allowed: true, incompleteCount: 0, message: '' };
    }
    const verb = incomplete.length > 1 ? 'are' : 'is';
    const plural = incomplete.length > 1 ? 's' : '';
    return {
      allowed: false,
      incompleteCount: incomplete.length,
      message: `Cannot complete this case. ${incomplete.length} Child Case${plural} ${verb} still incomplete. Please complete or cancel all Child Cases first.`,
    };
  }

  /** Resolve the "Open" status id — the canonical default for newly-created
   *  tasks and subtasks. Falls back to the first entry in the loaded list when
   *  the API doesn't expose an "Open" status. Never hardcoded. */
  getDefaultOpenStatusId(): number | null {
    const match = this.statusList.find(
      (s) => String(s?.status_name || '').toLowerCase().trim() === 'open'
    );
    if (match) return Number(match.task_status_id);
    return this.statusList[0] != null ? Number(this.statusList[0].task_status_id) : null;
  }

  /** Resolve a kanban-column key ('planned'/'in-progress'/'done'/'cancelled') to
   *  its API status id via the loaded list. Used when the user clicks a column's
   *  "+" button — the created task adopts that column's status. */
  getStatusIdByColumnKey(columnKey: string): number | null {
    const aliases: Record<string, string[]> = {
      'planned': ['open'],
      'in-progress': ['in progress', 'inprogress', 'in-progress'],
      'done': ['done', 'closed', 'complete', 'completed'],
      'cancelled': ['cancelled', 'canceled'],
    };
    const names = aliases[columnKey] || ['open'];
    for (const name of names) {
      const id = this.getStatusIdByName(name);
      if (id != null) return id;
    }
    return this.getDefaultOpenStatusId();
  }

  /** API status_name ("Open", "In Progress", "Done", "Cancelled") → kanban column id. */
  statusNameToColumnId(name: string | null | undefined): string {
    const n = String(name || '').toLowerCase().trim();
    if (n === 'open') return 'planned';
    if (n === 'in progress' || n === 'inprogress' || n === 'in-progress') return 'in-progress';
    if (n === 'done' || n === 'closed' || n === 'complete' || n === 'completed') return 'done';
    if (n === 'cancelled' || n === 'canceled') return 'cancelled';
    return '';
  }

  /** Centralized task update: merges an API response into the existing task in
   *  the tasks$ store, preserving counts that the API response doesn't include.
   *  Returns the merged TaskModel. All callers that receive fresh API data for
   *  a single task should route through this method instead of direct assignment. */
  updateTaskInStore(taskId: string | number, apiResponse: any): TaskModel {
    const stringId = String(taskId);
    const currentTasks = this.tasks$.value || [];
    const existing = currentTasks.find(t => t.task_id === stringId);
    const merged = this.mapApiResponseToTaskModel(apiResponse, existing);
    const updatedTasks = currentTasks.map(t => t.task_id === stringId ? merged : t);
    this.tasks$.next(updatedTasks);
    return merged;
  }

  /** Apply a count change to a task in the tasks$ store AND emit an event so
   *  the kanban board / table can update badges in real time. */
  emitAndApplyCountChange(taskId: number, commentCount?: number, attachmentCount?: number): void {
    const stringId = String(taskId);
    const currentTasks = this.tasks$.value || [];
    const updatedTasks = currentTasks.map(t => {
      if (t.task_id !== stringId) return t;
      const updated = {
        ...t,
        _raw: t._raw ? { ...t._raw } : undefined,
        _commentCount: commentCount !== undefined ? commentCount : t._commentCount,
        _attachmentCount: attachmentCount !== undefined ? attachmentCount : t._attachmentCount,
      };
      if (updated._raw) {
        if (commentCount !== undefined) updated._raw.comment_count = commentCount;
        if (attachmentCount !== undefined) updated._raw.attachment_count = attachmentCount;
      }
      return updated;
    });
    this.tasks$.next(updatedTasks);
    this.taskCountChanged$.next({ taskId, commentCount, attachmentCount });
  }

  /** Apply a count change to a subtask in the allSubtasks$ store AND emit an
   *  event so the subtask sidebar / compact cards can update badges in real time. */
  emitAndApplySubtaskCountChange(subtaskId: number, commentCount?: number, attachmentCount?: number): void {
    const current = this.allSubtasks$.getValue() || [];
    const updated = current.map((s: any) => {
      const id = s?.task_id ?? s?.id;
      if (Number(id) !== subtaskId) return s;
      const u = {
        ...s,
        comment_count: commentCount !== undefined ? commentCount : (s.comment_count ?? 0),
        attachment_count: attachmentCount !== undefined ? attachmentCount : (s.attachment_count ?? 0),
      };
      if (u._raw) {
        if (commentCount !== undefined) u._raw = { ...u._raw, comment_count: commentCount };
        if (attachmentCount !== undefined) u._raw = { ...u._raw, attachment_count: attachmentCount };
      }
      return u;
    });
    this.allSubtasks$.next(updated);
    // Resolve the subtask's parent_task_id from the store so subscribers can
    // refresh the correct master-detail grid row without a separate lookup.
    const subtaskRecord = updated.find((s: { task_id?: number; id?: number }) => Number(s?.task_id ?? s?.id) === subtaskId);
    const parentTaskId: number | null = subtaskRecord?.parent_task_id != null ? Number(subtaskRecord.parent_task_id) : null;
    this.subtaskCountChanged$.next({ subtaskId, parentTaskId, commentCount, attachmentCount });
  }

  /** Insert or replace a subtask in the in-memory store (matched by `task_id`). */
  updateSubtaskInStore(updatedSubtask: any): void {
    if (!updatedSubtask) return;
    const current = this.allSubtasks$.getValue();
    const idx = current.findIndex((s) => s?.task_id === updatedSubtask.task_id);
    if (idx > -1) {
      current[idx] = { ...current[idx], ...updatedSubtask };
    } else {
      current.push(updatedSubtask);
    }
    this.allSubtasks$.next([...current]);
  }

  /** Remove a subtask from the in-memory store by its task_id. */
  removeSubtaskFromStore(taskId: number): void {
    const current = this.allSubtasks$.getValue().filter((s) => s?.task_id !== taskId);
    this.allSubtasks$.next(current);
  }

  /** Load tasks once. Uses mock data when `useMockData` is true; otherwise hits the real API. */
  loadInitialTasks(): Observable<TaskModel[]> {
    if (this.loaded) return of(this.tasks$.getValue());

    if (this.useMockData) {
      // Legacy mock path kept to preserve the original method contract; the
      // in-bundle fixture array was deleted. useMockData is hardcoded false,
      // so this branch is unreachable in production.
      this.tasks$.next([]);
      this.loaded = true;
      return of([]);
    }

    return this.loadTasksFromApi().pipe(
      map((res: any) => {
        const tasks = this.mapApiResponseToTasks(res);
        this.tasks$.next(tasks);
        this.loaded = true;
        return tasks;
      })
    );
  }

  /** Get a single task by ID */
  getTaskById(taskId: string): Observable<TaskModel | undefined> {
    return this.tasks$.pipe(
      map(tasks => tasks.find(t => t.task_id === taskId))
    );
  }

  /** Observable for tasks */
  getTasks(): Observable<TaskModel[]> {
    return this.tasks$.asObservable();
  }

  /** Add new task. Uses mock when `useMockData` is true; otherwise calls the real API. */
  addTask(task: TaskModel): Observable<TaskModel> {
    if (this.useMockData) {
      const current = this.tasks$.value;
      const newTask: TaskModel = {
        ...task,
        task_id: "T" + (current.length + 1).toString().padStart(3, "0"),
      };
      this.tasks$.next([...current, newTask]);
      return of(newTask).pipe(delay(400)); // simulate API
    }

    const currentUser = this.getCurrentUserName();
    const payload = this.mapToCreatePayload(task, currentUser);
    return this.createTaskApi(payload).pipe(
      map((res: any) => {
        const created = this.mapApiResponseToTaskModel(res?.data ?? res) || task;
        const merged: TaskModel = {
          ...created,
          _commentCount: created._commentCount ?? 0,
          _attachmentCount: created._attachmentCount ?? 0,
          _raw: {
            ...(created._raw || {}),
          },
        };
        this.tasks$.next([...this.tasks$.value, merged]);
        return merged;
      })
    );
  }

  /** Update existing task. Uses mock when `useMockData` is true; otherwise calls the real API. */
  updateTask(task: TaskModel): Observable<TaskModel> {
    if (this.useMockData) {
      const updated = this.tasks$.value.map((t) =>
        t.task_id === task.task_id ? task : t
      );
      this.tasks$.next(updated);
      return of(task).pipe(delay(400));
    }

    const currentUser = this.getCurrentUserName();
    const payload = {
      ...this.mapToCreatePayload(task, currentUser),
      task_id: this.toNumericId(task.task_id),
    };
    return this.updateTaskApi(payload).pipe(
      map((res: any) => {
        const existing = this.tasks$.value.find(t => t.task_id === task.task_id);
        const updated = this.mapApiResponseToTaskModel(res?.data ?? res, existing) || task;
        const merged: TaskModel = {
          ...updated,
          _commentCount: updated._commentCount ?? existing?._commentCount ?? 0,
          _attachmentCount: updated._attachmentCount ?? existing?._attachmentCount ?? 0,
          _raw: {
            ...(existing?._raw || {}),
            ...(updated._raw || {}),
          },
        };
        if (merged._raw) {
          merged._raw.comment_count = merged._commentCount;
          merged._raw.attachment_count = merged._attachmentCount;
        }
        const list = this.tasks$.value.map((t) =>
          t.task_id === merged.task_id ? merged : t
        );
        this.tasks$.next(list);
        return merged;
      })
    );
  }

  // ==========================================================
  //  API <-> TaskModel mapping helpers
  //  All ID resolution is dynamic — driven by the lookup lists
  //  loaded via setStatusList / setPriorityList / setTypeList.
  // ==========================================================

  /** Build the create-task API payload. Public so dialog/sidebar callers can
   *  build payloads directly from form values. Status / priority / type fields
   *  are expected to already hold the numeric API ID (or a string of one). */
  mapToCreatePayload(task: any, currentUser: string): any {
    const toIntOrNull = (v: any): number | null => {
      if (v == null || v === '') return null;
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);
      return isNaN(n) ? null : n;
    };
    return {
      task_name: (task.taskName || task.task_name || '').toString().trim(),
      task_status_id: toIntOrNull(task.status ?? task.task_status_id),
      task_priority_id: toIntOrNull(task.priorityLevel ?? task.task_priority_id),
      task_type_id: toIntOrNull(task.taskType ?? task.task_type_id),
      description: ((task.description || '').toString().trim() || null),
      owner_id: task.assignTo || task.owner_id || null,
      start_date: task.startDate || task.start_date || null,
      end_date: task.endDate || task.end_date || null,
      remind_me: !!task.remindMe,
      remind_date: task.remindMe ? this.formatRemindDate(task.remindDate) : null,
      remind_time: task.remindMe ? (task.remindTime || null) : null,
      // TODO: Replace hardcoded brand_key with dynamic selection from the toolbar brand dropdown
      brand_key: 1,
      assignee_emails: Array.isArray(task.assignee_emails) && task.assignee_emails.length
        ? task.assignee_emails
        : (task.assignTo ? [task.assignTo] : (task.owner_id ? [task.owner_id] : [])),
      // API contract updated: tags now carry full objects so the backend can
      // persist tag_name + tag_category_id alongside the id in a single call.
      // Numeric-only arrays (legacy callers) map to an empty list — the rich
      // objects come from TaskDetailContentComponent.onSave.
      tags: Array.isArray(task.tags)
        ? task.tags
          .filter((t: any) => t && typeof t === 'object' && t.tag_id != null)
          .map((t: any) => ({
            tag_id: typeof t.tag_id === 'number' ? t.tag_id : parseInt(String(t.tag_id), 10),
            tag_name: t.tag_name || '',
            tag_category_id: t.tag_category_id != null
              ? (typeof t.tag_category_id === 'number' ? t.tag_category_id : parseInt(String(t.tag_category_id), 10))
              : null,
          }))
        : [],
      created_by: currentUser || null,
      updated_by: currentUser || null,
    };
  }

  /** Map an API response (array, {data:[]}, {tasks:[]}, {result:[]}) into TaskModel[]. */
  private mapApiResponseToTasks(res: any): TaskModel[] {
    let items: any[] = [];
    if (Array.isArray(res)) {
      items = res;
    } else if (Array.isArray(res?.data)) {
      items = res.data;
    } else if (Array.isArray(res?.tasks)) {
      items = res.tasks;
    } else if (Array.isArray(res?.result)) {
      items = res.result;
    }
    return items.map((item) => this.mapApiResponseToTaskModel(item));
  }

  /** Map a single API task object into a TaskModel.
   *  IDs are stored as strings on the model (status/priorityLevel/taskType) so
   *  they round-trip cleanly through Material form controls. The original API
   *  payload is preserved on `_raw` so templates can read task_status_name,
   *  task_priority_name, task_type_name directly. */
  mapApiResponseToTaskModel(item: any, existingTask?: TaskModel): TaskModel {
    if (!item) return {} as TaskModel;

    // owner_id from the new API shape is the assignee email.
    const assignTo: string = item.owner_id ?? item.assignTo ?? '';

    // Preserve the full assignees list so the UI can render stacked avatars.
    let assignees: { assignee_email: string }[] = [];
    if (Array.isArray(item.assignees) && item.assignees.length) {
      assignees = item.assignees
        .map((a: any) => ({ assignee_email: a?.assignee_email ?? a?.email ?? '' }))
        .filter((a: any) => !!a.assignee_email);
    } else if (Array.isArray(item.assignee_emails) && item.assignee_emails.length) {
      assignees = item.assignee_emails
        .filter((e: any) => !!e)
        .map((e: string) => ({ assignee_email: e }));
    } else if (assignTo) {
      assignees = [{ assignee_email: assignTo }];
    }

    // tags → number[] (rich objects passed through unchanged via _raw)
    let tags: number[] = [];
    if (Array.isArray(item.tags)) {
      tags = item.tags
        .map((t: any) => (typeof t === 'number' ? t : (t?.tag_id ?? t?.id)))
        .filter((n: any) => typeof n === 'number');
    } else if (Array.isArray(item.tag_ids)) {
      tags = item.tag_ids.filter((n: any) => typeof n === 'number');
    }

    const taskName = (item.task_name ?? item.taskName ?? '').toString().trim();
    const description = (item.description ?? '').toString().trim();

    const statusIdStr = item.task_status_id != null ? String(item.task_status_id) : '';
    const priorityIdStr = item.task_priority_id != null ? String(item.task_priority_id) : '';
    const typeIdStr = item.task_type_id != null ? String(item.task_type_id) : '';

    const statusNameLower = String(item.task_status_name || '').toLowerCase().trim();

    return {
      task_id: item.task_id != null ? String(item.task_id) : (item.id != null ? String(item.id) : ''),
      taskName,
      taskType: typeIdStr,
      assignTo,
      assignees,
      startDate: item.start_date ?? item.startDate ?? null,
      endDate: item.end_date ?? item.endDate ?? null,
      remindMe: item.remindMe ?? false,
      remindDate: item.remindDate ?? null,
      remindTime: item.remindTime ?? null,
      status: statusIdStr,
      priorityLevel: priorityIdStr,
      tagType: item.tagType ?? '',
      tags,
      description,
      taskStatus: statusNameLower === 'done',
      isCancelled: statusNameLower === 'cancelled' || statusNameLower === 'canceled',
      // Subtasks are loaded separately via the all-subtasks store.
      subtasks: [],
      _commentCount: (typeof item?.comment_count === 'number' && item.comment_count > 0)
        ? item.comment_count
        : (existingTask?._commentCount ?? existingTask?._raw?.comment_count ?? 0),
      _attachmentCount: (typeof item?.attachment_count === 'number' && item.attachment_count > 0)
        ? item.attachment_count
        : (existingTask?._attachmentCount ?? existingTask?._raw?.attachment_count ?? 0),
      _raw: (() => {
        const base = existingTask?._raw ? { ...existingTask._raw, ...item } : { ...item };
        const resolvedCC = (typeof item?.comment_count === 'number' && item.comment_count > 0)
          ? item.comment_count
          : (existingTask?._commentCount ?? existingTask?._raw?.comment_count ?? 0);
        const resolvedAC = (typeof item?.attachment_count === 'number' && item.attachment_count > 0)
          ? item.attachment_count
          : (existingTask?._attachmentCount ?? existingTask?._raw?.attachment_count ?? 0);
        base.comment_count = resolvedCC;
        base.attachment_count = resolvedAC;
        return base;
      })(),
    };
  }

  /** Best-effort numeric conversion of a TaskModel.task_id (string or stringified number). */
  toNumericId(id: string | number | null | undefined): number {
    if (id == null) return NaN;
    if (typeof id === 'number') return id;
    const n = parseInt(String(id).replace(/^T/i, ''), 10);
    return isNaN(n) ? NaN : n;
  }

  /** Format a Date object or date string to YYYY-MM-DD for the API payload. */
  private formatRemindDate(value: any): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value.substring(0, 10);
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return null;
  }

  /** Resolve the current user display name for created_by/updated_by. */
  private getCurrentUserName(): string {
    const fromSession = this.sessionService?.getUsername?.();
    if (fromSession) return fromSession;
    try {
      const raw = sessionStorage.getItem("userInformation");
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      return parsed?.usr_display_nm || '';
    } catch {
      return '';
    }
  }
}
